import { api } from "./client.js";

const REVIEWS = "/api/method/dynesourcerapi.reviews";

function unwrap(res) {
  return res?.data?.message ?? res?.data ?? res;
}

function frappeError(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "Request failed";
  if (typeof data.message === "string") return data.message;
  if (data._server_messages) {
    try {
      const first = JSON.parse(JSON.parse(data._server_messages)[0]);
      if (first?.message) return String(first.message).replace(/<[^>]*>/g, "");
    } catch {
      /* ignore */
    }
  }
  if (typeof data.exception === "string") {
    return data.exception.split("\n").pop() || data.exception;
  }
  return err?.message || "Request failed";
}

export const CALLBACK_REVIEW_TYPE = "Callback Request";

export function buildCallbackReviewText({
  name,
  phone,
  email,
  productIds = [],
  category,
} = {}) {
  const ids = (Array.isArray(productIds) ? productIds : [productIds]).filter(Boolean);
  return [
    "Callback request",
    "",
    name ? `Name: ${name}` : null,
    phone ? `Phone: ${phone}` : null,
    email ? `Email: ${email}` : null,
    ids.length ? `Product IDs: ${ids.join(", ")}` : null,
    category ? `Category: ${category}` : null,
    "",
    "Please call me back. I am not sure which shortlisted product suits me and would like our team to review my shortlist.",
  ]
    .filter((line) => line !== null)
    .join("\n");
}

export const CALLBACK_CONTACTED_NOTE = "Contacted";
export const CALLBACKS_CHANGED_EVENT = "ds-callbacks-changed";

export function notifyCallbacksChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CALLBACKS_CHANGED_EVENT));
  }
}

export function isCallbackContacted(review = {}) {
  const note = String(review.admin_response || review.admin_note || "");
  return /contacted/i.test(note);
}

export function isCallbackPending(review = {}) {
  if (!parseCallbackReview(review).isCallback) return false;
  if (isCallbackContacted(review)) return false;
  const status = String(review.status || review.review_status || "Pending").toLowerCase();
  return !/reject|spam|hidden/.test(status);
}

export function parseCallbackReview(review = {}) {
  const text = String(review.review_text || "");
  const pick = (re) => {
    const match = text.match(re);
    return match ? String(match[1] || "").trim() : "";
  };
  const type = String(review.review_type || review.title || "");
  const isCallback =
    /callback/i.test(type) || /^callback request/i.test(text.trim());
  return {
    isCallback,
    name: review.reviewer_name || pick(/^Name:\s*(.+)$/im) || "",
    phone:
      review.phone ||
      review.phone_number ||
      review.reviewer_phone ||
      pick(/^Phone:\s*(.+)$/im) ||
      "",
    email:
      review.email ||
      review.reviewer_email ||
      review.user ||
      pick(/^Email:\s*(.+)$/im) ||
      "",
    productIds:
      review.product ||
      pick(/^Product IDs?:\s*(.+)$/im) ||
      "",
  };
}

/**
 * Public: submit a review / feedback.
 * POST dynesourcerapi.reviews.submit_review
 */
export async function submitReview(payload = {}) {
  const body = {
    review_text: String(payload.review_text || "").trim(),
    rating: payload.rating != null && payload.rating !== "" ? Number(payload.rating) : undefined,
    title: payload.title ? String(payload.title).trim() : undefined,
    review_type: payload.review_type || "General Feedback",
    product_category: payload.product_category || undefined,
    product: payload.product || undefined,
    reviewer_name: payload.reviewer_name || undefined,
    page_url: payload.page_url || (typeof window !== "undefined" ? window.location.pathname : undefined),
  };
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  if (!body.review_text) throw new Error("Please enter your feedback.");
  if (body.rating != null && (body.rating < 1 || body.rating > 5)) {
    throw new Error("Rating must be between 1 and 5.");
  }

  try {
    const res = await api.post(`${REVIEWS}.submit_review`, body, {
      headers: { Authorization: "" },
    });
    return unwrap(res);
  } catch (err) {
    throw new Error(frappeError(err));
  }
}

/**
 * Public: list published reviews.
 */
export async function getReviews(params = {}) {
  try {
    const res = await api.post(`${REVIEWS}.get_reviews`, params, {
      headers: { Authorization: "" },
    });
    const msg = unwrap(res) || {};
    return {
      ok: Boolean(msg.ok ?? true),
      data: Array.isArray(msg.data) ? msg.data : [],
      pagination: msg.pagination || null,
      summary: msg.summary || null,
    };
  } catch (err) {
    throw new Error(frappeError(err));
  }
}

export async function getReviewSummary(params = {}) {
  try {
    const res = await api.post(`${REVIEWS}.get_review_summary`, params, {
      headers: { Authorization: "" },
    });
    return unwrap(res) || {};
  } catch (err) {
    throw new Error(frappeError(err));
  }
}

export async function markReviewHelpful(reviewName) {
  try {
    const res = await api.post(
      `${REVIEWS}.mark_review_helpful`,
      { review: reviewName, name: reviewName },
      { headers: { Authorization: "" } }
    );
    return unwrap(res);
  } catch (err) {
    throw new Error(frappeError(err));
  }
}

/**
 * Admin: list all reviews (requires admin session).
 */
export async function listReviewsAdmin(params = {}) {
  try {
    const res = await api.post(`${REVIEWS}.list_reviews_admin`, {
      page: params.page ?? 1,
      page_size: params.page_size ?? 50,
      status: params.status || undefined,
      review_type: params.review_type || undefined,
      product_category: params.product_category || undefined,
      search: params.search || undefined,
    });
    const msg = unwrap(res) || {};
    return {
      ok: Boolean(msg.ok ?? true),
      data: Array.isArray(msg.data) ? msg.data : Array.isArray(msg) ? msg : [],
      pagination: msg.pagination || null,
      summary: msg.summary || null,
      raw: msg,
    };
  } catch (err) {
    // Fallback to public list if admin method unavailable
    const message = frappeError(err);
    if (/not whitelisted|has no attribute|PermissionError/i.test(message)) {
      const pub = await getReviews({
        page: params.page ?? 1,
        page_size: params.page_size ?? 50,
      });
      return { ...pub, fallback: true };
    }
    throw new Error(message);
  }
}

/**
 * Admin: set review status / response.
 */
export async function setReviewStatus({ review, status, admin_response } = {}) {
  try {
    const res = await api.post(`${REVIEWS}.set_review_status`, {
      review,
      name: review,
      status,
      ...(admin_response !== undefined ? { admin_response } : {}),
    });
    return unwrap(res);
  } catch (err) {
    throw new Error(frappeError(err));
  }
}

const ENGAGEMENT = "/api/method/dynesourcerapi.engagement_tracking";

/** Canonical sourcing page keys expected by engagement_tracking.track_engagement */
export const ENGAGEMENT_PAGES = {
  BTL: "/mortgages/buy-to-let-mortgage-calculator",
  IF: "/invoice-finance/invoice-finance-sourcing",
};

const ENGAGEMENT_TRACK_URL = `${ENGAGEMENT}.track_engagement`;

function buildEngagementRequest(payload = {}) {
  const page =
    payload.page ||
    payload.page_path ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  if (!page) return { page: "", body: null };

  const body = {
    page,
    section: payload.section || undefined,
    seconds:
      payload.seconds != null && payload.seconds !== ""
        ? Number(payload.seconds)
        : undefined,
    scroll_percent:
      payload.scroll_percent != null && payload.scroll_percent !== ""
        ? Number(payload.scroll_percent)
        : undefined,
    event_type: payload.event_type || undefined,
    product_category: payload.product_category || undefined,
    product: payload.product || undefined,
  };
  Object.keys(body).forEach((k) => body[k] === undefined && delete body[k]);

  return { page, body };
}

function trackEngagementBeacon(page, body) {
  if (typeof window === "undefined" || !page || !body) {
    return { ok: false, skipped: true, reason: "missing_page" };
  }

  const url = `${ENGAGEMENT_TRACK_URL}?page=${encodeURIComponent(page)}`;

  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      const sent = navigator.sendBeacon(url, blob);
      return { ok: sent, data: { beacon: true } };
    }

    if (typeof fetch === "function") {
      void fetch(url, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        credentials: "include",
        keepalive: true,
      });
      return { ok: true, data: { keepalive: true } };
    }
  } catch (err) {
    console.warn("[trackEngagementBeacon]", err?.message || err);
    return { ok: false, error: err?.message || "beacon_failed" };
  }

  return { ok: false, skipped: true, reason: "beacon_unavailable" };
}

/**
 * Guest: track engagement for a sourcing page.
 * API: dynesourcerapi.engagement_tracking.track_engagement?page=...
 * Optional body fields: section, seconds, scroll_percent.
 * Pass delivery: "beacon" for unload-safe delivery.
 */
export async function trackEngagement(payload = {}, options = {}) {
  try {
    const { page, body } = buildEngagementRequest(payload);
    if (!page || !body) return { ok: false, skipped: true, reason: "missing_page" };

    if (options.delivery === "beacon") {
      return trackEngagementBeacon(page, body);
    }

    const res = await api.post(ENGAGEMENT_TRACK_URL, body, {
      headers: { Authorization: "" },
      params: { page },
    });
    return { ok: true, data: unwrap(res) };
  } catch (err) {
    const message = frappeError(err);
    if (/has no attribute|not whitelisted|Failed to get method/i.test(message)) {
      return { ok: false, skipped: true, reason: "not_deployed", message };
    }
    console.warn("[trackEngagement]", message);
    return { ok: false, error: message };
  }
}

/**
 * Admin: engagement analytics rollup.
 * API: dynesourcerapi.engagement_tracking.get_engagement_analytics
 */
export async function getEngagementAnalytics(params = {}) {
  try {
    const res = await api.get(`${ENGAGEMENT}.get_engagement_analytics`, { params });
    const msg = unwrap(res) || {};
    return {
      ok: Boolean(msg.ok ?? true),
      data: msg,
    };
  } catch (err) {
    // Some Frappe setups only allow POST for methods
    try {
      const res = await api.post(`${ENGAGEMENT}.get_engagement_analytics`, params);
      const msg = unwrap(res) || {};
      return { ok: Boolean(msg.ok ?? true), data: msg };
    } catch (err2) {
      const message = frappeError(err2);
      if (/has no attribute|not whitelisted|Failed to get method/i.test(message)) {
        return { ok: false, skipped: true, reason: "not_deployed", message, data: null };
      }
      throw new Error(message);
    }
  }
}

export async function submitCallbackRequest(payload = {}) {
  const productIds = (Array.isArray(payload.productIds) ? payload.productIds : [payload.productIds])
    .map((id) => String(id || "").trim())
    .filter(Boolean);
  const body = {
    review_text: buildCallbackReviewText({
      name: payload.name,
      phone: payload.phone,
      email: payload.email,
      productIds,
      category: payload.category,
    }),
    title: "Callback request",
    review_type: CALLBACK_REVIEW_TYPE,
    product_category: payload.category || undefined,
    reviewer_name: payload.name || undefined,
    page_url:
      payload.pageUrl ||
      (typeof window !== "undefined" ? window.location.pathname : undefined),
  };

  try {
    return await submitReview(body);
  } catch (err) {
    const message = String(err?.message || "");
    if (/invalid|not in list|review_type|permitted value/i.test(message)) {
      return submitReview({ ...body, review_type: "Suggestion" });
    }
    throw err;
  }
}

export const REVIEW_TYPES = [
  "Product Review",
  "General Feedback",
  "Suggestion",
  "Complaint",
  "Bug Report",
  CALLBACK_REVIEW_TYPE,
];
